<script lang="ts" setup>
import { useProductCounter } from '@/composables/useProductCounter'

import { useCityStore } from '@/store/city/city'

import { ClientPack } from '@/types/api/api-v3/basket'

const { currencySymbol } = useCityStore()

type Props = {
  clientPack: ClientPack
  isCardView?: boolean
}

const props = defineProps<Props>()

const { isMobile } = useDevice()

const clientPackImage = computed(() =>
  props?.clientPack?.image_url
    ? props.clientPack.image_url
    : '/no-image-category.webp',
)
// Нужен для миксина "ProductCounter"
const productId = computed(() => props?.clientPack?.product_id as number)

const counterSize = computed(() => {
  if (!props.isCardView) return 'sm'

  return isMobile ? 'xs' : 'sm'
})

const {
  decreaseQuantity,
  increaseQuantity,
  setProductQuantity,
  quantitySelectedProducts,
} = useProductCounter(productId)

onMounted(() => {
  const productQuantity = props?.clientPack?.total_quantity || 0

  setProductQuantity(productQuantity)
})
</script>

<template>
  <div
    class="client-pack-item"
    :class="{
      'client-pack-item--card': isCardView,
    }"
  >
    <nuxt-img :src="clientPackImage" alt="Приборы" />

    <div class="client-pack-item__text" :title="clientPack?.title">
      {{ clientPack?.title }}
    </div>

    <div class="client-pack-item__price">
      {{ clientPack?.total_price }} {{ currencySymbol }}
    </div>

    <div class="client-pack-item__counter">
      <the-counter
        :min="0"
        :value="quantitySelectedProducts"
        :variant="isCardView ? 'secondary' : 'primary'"
        :size="counterSize"
        @on-decrease="decreaseQuantity"
        @on-increase="increaseQuantity"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.client-pack-item {
  display: grid;
  grid-template-rows: 1fr auto;
  grid-template-columns: auto 1fr max-content;
  margin-bottom: 16px;

  @include media('>=tablet') {
    margin-bottom: 20px;
  }

  img {
    grid-area: 1 / 1 / 3 / 2;
    width: 59px;

    @include media('>=tablet') {
      width: 76px;
    }
  }

  &__text {
    display: flex;
    grid-area: 1 / 2 / 2 / 3;
    align-items: center;
    margin-left: 20px;
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
    text-align: left;

    @include media('>=tablet') {
      width: 268px;
      font-size: 14px;
    }
  }

  &__price {
    display: flex;
    grid-area: 2 / 2 / 3 / 3;
    align-items: center;
    margin-left: 22px;
    font-size: 14px;
    font-weight: 800;
    line-height: 20px;
    @include media('>=tablet') {
      width: 108px;
      margin-left: 20px;
    }
  }

  &__counter {
    display: flex;
    grid-area: 1 / 3 / 3 / 4;
    align-items: center;
    justify-content: flex-end;
    margin-left: 20px;
  }

  &--card {
    display: grid;
    grid-template-rows: repeat(4, max-content);
    grid-template-columns: 1fr;
    width: 100%;
    max-width: 128px;
    max-height: 206px;
    padding: 8px;
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    background-color: #f1f4f9;
    border-radius: 16px;

    img {
      grid-area: unset;
      width: 100%;
      height: 76px;
      object-fit: contain;
    }

    .client-pack-item__text {
      display: block;
      grid-area: 3 / 1 / 3 / 1;
      width: 100%;
      margin-bottom: 16px;
      margin-left: 0;
      overflow: hidden;
      line-height: unset;
      color: var(--ui-text-primary);
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .client-pack-item__price {
      display: block;
      grid-area: 2 / 1 / 2 / 1;
      margin: 8px 0 5px;
      font-weight: 800;
      line-height: unset;
      color: var(--ui-text-primary);
    }

    .client-pack-item__counter {
      display: block;
      grid-area: unset;
      margin-left: 0;

      :deep(.app-counter) {
        min-width: 100%;
        height: 40px;
      }
    }
  }
}
</style>
